import React, { useCallback, useEffect, useMemo, useState } from "react";
import CenterSpinner from "../../../../../general/Loadings/CenterSpinner";
import { callTokenApi } from "../../../../../../apiCaller";
import {
    API_AI_INSIGHTS_INTENT_PROPERTIES,
    API_AI_INSIGHTS_INTENT_RULES,
    API_AI_INSIGHTS_INTENT_DATASETS,
    API_CLIENT_GET_LIST_SEVER_SIDE_TRIGGER,
    TYPE_SHOW_UNSAVE_CHANGE,
    API_INTELLIGENCE_LLM_MODELS,
} from "../../../../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { setRuleHaveEditting } from "../../../../../../actions/common";
import { setListModels } from "../../../../../../actions/subscriber";

const AnalysisType = React.lazy(() => import("./steps/AnalysisType"));
const SettingNameDescription = React.lazy(() => import("./steps/SettingNameDescription"));
const DataInput = React.lazy(() => import("./steps/DataInput"));
const Instructions = React.lazy(() => import("./steps/Instructions"));
const Triggers = React.lazy(() => import("./steps/Triggers"));
const ModelSelectionPreview = React.lazy(() => import("./steps/ModelSelectionPreview"));

export const ManageIntentRulesContext = React.createContext({});

const fetchProperties = (accountId, propertyId) => {
    const query = propertyId ? `?excludeId=${propertyId}` : '';
    const url = `${API_AI_INSIGHTS_INTENT_PROPERTIES}/${accountId}${query}`;

    return callTokenApi(url, "GET").then((res) =>
        res?.status === 200
            ? (res.data?.data || [])
                  .filter((item) => item.status)
                  .map((item) => ({
                      value: item.id,
                      label: item.name,
                      object: item.object.toLowerCase(),
                      fieldType: item.fieldType,
                  }))
            : []
    );
};

const fetchDatasets = (accountId) =>
    callTokenApi(`${API_AI_INSIGHTS_INTENT_DATASETS}/account/${accountId}`, "GET").then(
        (res) => res?.status === 200 ? (res.data?.data || [])
            .filter((item) => item.status)    
            .map((item) => ({ value: item.id, label: item.name, datasets: item.dataset_data?.[0] ?? [] })) : []
    );

const fetchTriggers = (accountId) =>
    callTokenApi(`${API_CLIENT_GET_LIST_SEVER_SIDE_TRIGGER.replace(":accountId", accountId)}`, "GET").then(
        (res) => res?.status === 200 ? (res.data?.triggers || []).filter((item) => item.status).map((item) => ({ value: item.id, label: item.name, targetObject: item.targetObject.toLowerCase() })) : []
    );

const fetchRule = (id) =>
    callTokenApi(`${API_AI_INSIGHTS_INTENT_RULES}/${id}`, "GET").then(
        (res) => (res?.status === 200 ? res.data?.data : null)
    );

const fetchModels = async () => {
    const response = await callTokenApi(API_INTELLIGENCE_LLM_MODELS, 'GET');
    if (response.status === 200 && response.data?.data) {
        return response.data.data;
    }
};

const useInitData = (accountId, ruleId) => {
    const dispatch = useDispatch();
    const models = useSelector((state) => state.subscriber.listModels);

    const [loading, setLoading] = useState(true);
    const [intentProperties, setIntentProperties] = useState([]);
    const [intentDatasets, setIntentDatasets] = useState([]);
    const [serverSideTriggers, setServerSideTriggers] = useState([]);
    const [ruleData, setRuleData] = useState(null);

    useEffect(() => {
        if (!accountId) return;

        const init = async () => {
            setLoading(true);

            let rule = null;

            if (ruleId) {
                rule = await fetchRule(ruleId);
                setRuleData(rule);
            }

            const [
                props,
                datasets,
                triggers,
                modelsResponse,
            ] = await Promise.all([
                fetchProperties(accountId, rule?.propertyId),
                fetchDatasets(accountId),
                fetchTriggers(accountId),
                models.length === 0 ? fetchModels() : [],
            ]);

            setIntentProperties(props);
            setIntentDatasets(datasets);
            setServerSideTriggers(triggers);
            setLoading(false);

            if (modelsResponse.length > 0) {
                dispatch(setListModels(modelsResponse));
            }
        };

        init();
    }, [accountId, ruleId]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        loading,
        intentProperties,
        intentDatasets,
        serverSideTriggers,
        ruleData,
    };
};

const ManageIntentRules = ({ attributes }) => {
    const { ruleId: id, setRefetch, setListRules, fetchIntentRules } = attributes;
    const dispatch = useDispatch();
    const models = useSelector((state) => state.subscriber.listModels);
    const activeAccount = useSelector((state) => state.subscriber.activeAccount);

    const {
        loading: isLoading,
        intentProperties,
        intentDatasets,
        serverSideTriggers,
        ruleData,
    } = useInitData(activeAccount?.accountId, id);

    const [activeStep, setActiveStep] = useState(1);
    const [stepsData, setStepsData] = useState({});
    const [dirtySteps, setDirtySteps] = useState({});
    const [analysisType, setAnalysisType] = useState();
    const [initStepsData, setInitStepsData] = useState({});
    const [stepSubmitSnapshot, setStepSubmitSnapshot] = useState({});
    const [optionProperties, setOptionProperties] = useState(intentProperties || []);
    const [responseData, setResponseData] = useState([]);
    const [state, setState] = useState({
        accordion: '',
        isSubmitting: false,
        modelSelections: [{ id: '', exampleOutput: '', isLoading: false }],
    });

    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    useEffect(() => {
        if (id && ruleData) {
            setStepsData(ruleData);
            setInitStepsData(ruleData);
            setAnalysisType(ruleData.type);
            setActiveStep(2);
            setOptionProperties(intentProperties);
        }
        if (!id) {
            setStepsData({});
            setInitStepsData({});
            setAnalysisType(undefined);
            setActiveStep(1);
            setOptionProperties(intentProperties);
        }
    }, [id, ruleData, intentProperties]);

    const setStepDirty = useCallback((step, dirty) => {
        setDirtySteps((prev) => {
            const next = { ...prev, [step]: dirty };
            const show = Object.values(next).some(Boolean);
            dispatch(
                setRuleHaveEditting({
                    show,
                    type: show ? TYPE_SHOW_UNSAVE_CHANGE.CREATE_SIMPLE : "",
                })
            );
            return next;
        });
    }, [dispatch]);

    const contextValue = useMemo(
        () => ({
            id,
            activeStep,
            setActiveStep,
            initStepsData,
            setInitStepsData,
            stepsData,
            setStepsData,
            analysisType,
            setAnalysisType,
            intentProperties,
            setListRules,
            fetchIntentRules,
            intentDatasets,
            serverSideTriggers,
            onFormDirty: setStepDirty,
            dirtySteps,
            setDirtySteps,
            stepSubmitSnapshot,
            setStepSubmitSnapshot,
            setRefetch,
            optionProperties,
            setOptionProperties,
            responseData,
            setResponseData,
            state,
            setState
        }),
        [
            id, activeStep, initStepsData, stepsData, analysisType,
            intentProperties, setListRules, fetchIntentRules, intentDatasets, serverSideTriggers,
            setStepDirty, dirtySteps, stepSubmitSnapshot, setRefetch, optionProperties, setOptionProperties,
            responseData, state
        ]
    );

    return (
        <div className="rule-detail account-connected-detail">
            {isLoading ? (
                <CenterSpinner />
            ) : (
                <div className="cvr-create-new">
                    <h1>{id ? `Edit Intent Rule: ${stepsData.name || ""}` : `Create Intent Rule`}</h1>
                    <p>
                        Create and manage an AI analysis rule that will classify or summarize activity on an object in your ListenLayer data.
                    </p>
                    <ManageIntentRulesContext.Provider value={contextValue}>
                        <React.Suspense fallback={<CenterSpinner />}>
                            <AnalysisType />
                            {!!analysisType && (
                                <>
                                    <SettingNameDescription />
                                    <DataInput />
                                    <Instructions />
                                    <Triggers />
                                    <ModelSelectionPreview models={models} />
                                </>
                            )}
                        </React.Suspense>
                    </ManageIntentRulesContext.Provider>
                </div>
            )}
        </div>
    );
};

export default ManageIntentRules;
